import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { AuthService } from '../../core/services/auth.service';
import { AppointmentService } from '../../core/services/appointment.service';
import { Appointment } from '../../core/models/appointment.model';
import { CalendarService } from '../../core/services/calendar.service';

@Component({
  selector: 'app-my-appointments',
  standalone: false,
  templateUrl: './my-appointments.component.html',
  styleUrl: './my-appointments.component.scss',
})
export class MyAppointmentsComponent implements OnInit, OnDestroy {
  upcoming: Appointment[] = [];
  past: Appointment[] = [];
  activeTab: 'upcoming' | 'past' = 'upcoming';
  cancellingId: string | null = null;
  isLoading = true;

  private sub = new Subscription();

  constructor(
    public auth: AuthService,
    private apptSvc: AppointmentService,
    private router: Router,
    private calendarSvc: CalendarService,
  ) {}

  ngOnInit(): void {
    const user = this.auth.currentUserSnapshot;
    if (!user) { this.router.navigate(['/login']); return; }

    this.sub.add(
      this.apptSvc.byClient$(user.id).subscribe(apts => {
        const today = new Date().toISOString().split('T')[0];
        const sorted = [...apts].sort((a, b) =>
          (a.date + a.time).localeCompare(b.date + b.time)
        );
        this.upcoming = sorted.filter(a => a.date >= today && a.status !== 'cancelled');
        this.past     = sorted.filter(a => a.date < today || a.status === 'cancelled')
                               .reverse();
        this.isLoading = false;
      })
    );
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  cancelAppointment(apt: Appointment): void {
    this.cancellingId = apt.id;
    // Simula confirmación antes de cancelar
    setTimeout(() => {
      this.apptSvc.cancel(apt.id);
      this.cancellingId = null;
    }, 300);
  }

  reschedule(apt: Appointment): void {
    this.router.navigate(['/booking'], {
      queryParams: { reschedule: apt.id }
    });
  }

  formatDateFull(dateStr: string): string {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('es-AR', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    });
  }

  formatDateShort(dateStr: string): string {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('es-AR', {
      weekday: 'short', day: 'numeric', month: 'short',
    });
  }

  getDayNumber(dateStr: string): string {
    return new Date(dateStr + 'T00:00:00').getDate().toString();
  }

  getMonthShort(dateStr: string): string {
    return new Date(dateStr + 'T00:00:00')
      .toLocaleDateString('es-AR', { month: 'short' })
      .replace('.', '');
  }

  statusLabel(status: Appointment['status']): string {
    const map: Record<Appointment['status'], string> = {
      pending:   'Pendiente',
      confirmed: 'Confirmado',
      cancelled: 'Cancelado',
    };
    return map[status];
  }

  get displayList(): Appointment[] {
    return this.activeTab === 'upcoming' ? this.upcoming : this.past;
  }

  addToGoogleCalendar(apt: Appointment): void {
    this.calendarSvc.openGoogleCalendarLink(apt);
  }

  downloadIcs(apt: Appointment): void {
    this.calendarSvc.downloadIcs(apt);
  }
}
